/**
 * ACCENT COLOUR HELPERS — for cards that are painted in a colour the OPERATOR
 * chose (a package's solid/gradient, a product's theme), not one we picked.
 *
 * Because the colour is arbitrary, nothing here may assume it is dark: a
 * package saved as pale yellow would render white-on-white text if the ink
 * were hardcoded. So text colour is DERIVED from relative luminance
 * (WCAG 2.x formula) rather than assumed, and any accent used as text on a
 * white surface is deepened until it is actually readable.
 */

const HEX = /^#?([a-f\d]{3}|[a-f\d]{6})$/i;

/** Parse any hex form to RGB. Falls back to ruby for anything unparseable. */
export function toRgb(hex: string): [number, number, number] {
  const m = HEX.exec((hex || '').trim());
  if (!m) return [220, 20, 60];
  let h = m[1];
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const n = parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** `rgba()` at the given alpha — safe for any hex form, unlike `${hex}20`. */
export function tint(hex: string, alpha: number): string {
  const [r, g, b] = toRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** WCAG relative luminance, 0 (black) → 1 (white). */
export function luminance(hex: string): number {
  const [r, g, b] = toRgb(hex).map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Mix a colour toward black by `amount` (0–1). */
export function shade(hex: string, amount: number): string {
  const [r, g, b] = toRgb(hex);
  const f = (v: number) => Math.round(v * (1 - amount));
  return `rgb(${f(r)}, ${f(g)}, ${f(b)})`;
}

/**
 * Text colour to sit ON a block of `hex` — white for dark fills, near-black
 * for light ones. This is what stops a pale accent producing invisible text.
 */
export function inkOn(hex: string): string {
  return luminance(hex) > 0.55 ? '#1c1917' : '#ffffff';
}

/**
 * The accent used as TEXT on a white surface. A light accent (yellow, mint)
 * is deepened until it reads; a dark one is returned untouched.
 */
export function readableAccent(hex: string): string {
  const l = luminance(hex);
  if (l <= 0.32) return hex;
  return shade(hex, Math.min(0.62, 0.28 + l * 0.55));
}

export interface Accent {
  /** Primary identity colour. */
  from: string;
  /** Gradient end (equals `from` for a solid). */
  to: string;
  /** Ready-to-use `background` for the card's colour band. */
  band: string;
  /** Text/icon colour that is readable on the band. */
  ink: string;
  /** The accent, deepened enough to use as text on white. */
  onWhite: string;
  isGradient: boolean;
}

/**
 * Resolve an entity's colour fields into everything a card needs.
 * Accepts the package shape (`colorType`/`color`/`gradientFrom`/`gradientTo`)
 * and any caller that passes the same field names.
 */
export function resolveAccent(src: {
  colorType?: 'solid' | 'gradient' | string;
  color?: string;
  gradientFrom?: string;
  gradientTo?: string;
}, fallback = '#DC143C'): Accent {
  const isGradient = src.colorType === 'gradient';
  const from = (isGradient ? src.gradientFrom : src.color) || src.color || src.gradientFrom || fallback;
  const to = (isGradient ? src.gradientTo : src.color) || from;
  return {
    from,
    to,
    isGradient,
    band: isGradient ? `linear-gradient(135deg, ${from}, ${to})` : from,
    ink: inkOn(from),
    onWhite: readableAccent(from),
  };
}
